import { useState } from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaMapMarkerAlt, FaPaperPlane } from "react-icons/fa";

export default function Contact() {
  const config = {
    email: "info.vcodefreelance@gmail.com",
    address: "Coimbatore, Tamil Nadu, India",
  };

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    industry: "",
    message: "",
  });

  const isFormValid =
    formData.name.trim() &&
    formData.surname.trim() &&
    formData.email.trim() &&
    formData.industry.trim() &&
    formData.message.trim();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      className="relative bg-gradient-to-b from-black to-gray-900 text-white py-24 px-6"
    >
      {/* Floating Animation Elements */}
      <motion.div
        animate={{ x: [0, 10, -10, 0], rotate: [0, 10, -10, 0] }}
        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
        className="hidden sm:block absolute top-10 left-10 w-16 h-16 bg-blue-500/30 rounded-full blur-2xl"
      ></motion.div>

      <motion.div
        animate={{ y: [0, -15, 15, 0], rotate: [0, -5, 5, 0] }}
        transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
        className="hidden sm:block absolute bottom-10 right-10 w-12 h-12 bg-purple-500/30 rounded-full blur-2xl"
      ></motion.div>

      {/* Section Title */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl sm:text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
          Contact Us
        </h1>
        <p className="text-lg text-gray-300 mt-3">
          Ready to start your project? Get in touch with us today!
        </p>
      </motion.div>

      {/* Grid Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-8 flex flex-col items-center text-center"
          >
            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <FaEnvelope className="text-2xl text-blue-400" />
                <p className="text-lg">
                  <span className="font-semibold">Email: </span>
                  <a
                    href={`mailto:${config.email}`}
                    className="text-blue-300 hover:underline"
                  >
                    {config.email}
                  </a>
                </p>
              </div>

              <div className="flex items-center space-x-4">
                <FaMapMarkerAlt className="text-2xl text-red-400" />
                <p className="text-lg">
                  <span className="font-semibold">Address: </span>
                  {config.address}
                </p>
              </div>
            </div>

            {/* Motivational Tagline */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-center space-y-3"
            >
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-yellow-300 to-orange-400 text-transparent bg-clip-text">
                Let's Work Together!
              </h2>
              <p className="text-lg text-gray-300">
                Your Ideas, Our Code, Turning visions into reality.
              </p>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            action="https://formspree.io/f/xnnpabal"
            method="POST"
            className="w-full max-w-xl mx-auto bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-blue-500/40 space-y-6"
          >
            {/* Name */}
            <div className="space-y-2">
              <label className="block text-white text-lg font-medium">Name</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                placeholder="Enter your name"
              />
            </div>

            {/* Surname */}
            <div className="space-y-2">
              <label className="block text-white text-lg font-medium">Surname</label>
              <input
                type="text"
                name="surname"
                required
                value={formData.surname}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                placeholder="Enter your surname"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-white text-lg font-medium">Mail ID</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                placeholder="Enter your email"
              />
            </div>

            {/* Industry */}
            <div className="space-y-2">
              <label className="block text-white text-lg font-medium">Industry</label>
              <input
                type="text"
                name="industry"
                required
                value={formData.industry}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                placeholder="Your industry (e.g., Tech, Finance, Healthcare)"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="block text-white text-lg font-medium">Your Message</label>
              <textarea
                name="message"
                required
                rows="4"
                value={formData.message}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                placeholder="Enter your message"
              ></textarea>
            </div>

            {/* Submit */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              type="submit"
              disabled={!isFormValid}
              className={`w-full flex items-center justify-center gap-2 px-6 py-3 text-lg font-semibold rounded-full shadow-lg transition duration-300
                ${isFormValid ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-gray-600 cursor-not-allowed"}
              `}
            >
              <FaPaperPlane className="text-lg" />
              Send Message
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
